import { findViolationTypeFromID } from "helpers/sf_lead_constants"
import { REPORTING_TIMEZONE, LEAD_TYPE_IDS, NAME_OF_LEAD_TYPES } from "helpers/sf_constants"

const keyOf = value =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
const LIST_TYPES = ["leads", "calls", "prospects"]
const LEAD_TYPE_NAMES = Object.fromEntries(
  Object.entries(LEAD_TYPE_IDS).map(([name, id]) => [id, NAME_OF_LEAD_TYPES[name]])
)
const MONEY = new Set([
  "cost",
  "price",
  "subsidy",
  "outofpocketcost",
  "revenue",
  "spend",
  "payout",
  "leadcost",
  "amountpaid",
])
const NUMERIC = new Set([
  ...MONEY,
  "clicks",
  "calls",
  "leads",
  "impressions",
  "conversions",
  "searches",
  "applicants",
  "vehicles",
  "callduration",
  "ctr",
  "winrate",
  "bid",
  "cpc",
])

const COLUMN_ALIASES = {
  prospect: "name",
  territory: "adgroupname",
  profile: "adgroupname",
  profilename: "campaignname",
  brand: "brandname",
  type: "leadtypename",
  zipcode: "zip",
  received: "createdat",
  numberofemployees: "numemployees",
  callstatus: "status",
  callrecording: "recordingurl",
  return: "refundstatus",
  refund: "refundstatus",
  imprs: "impressions",
  avgpos: "avgposition",
  avgcpc: "cpc",
  avgbid: "bid",
  totalpayout: "cost",
}

const ANALYTICS_COLUMN_ALIASES = {
  sourcetype: "sourcetypename",
  time: "timestamp",
  age: "adminconsumerage",
  credit: "admincredit",
  education: "admineducation",
  gender: "admingender",
  homeowner: "adminhomeowner",
  licenseactive: "adminlicensestatus",
  married: "adminmaritalstatus",
  numberofdrivers: "adminnumdrivers",
  numberofvehicles: "adminnumvehicles",
  insured: "admininsured",
  continuouscoverage: "admincontinuouscoverage",
  numberofemployees: "adminnumemployees",
  bankruptcy: "adminbankruptcy",
  occupation: "adminoccupation",
  yearslicensed: "adminyearslicensed",
  annualmileage: "adminannualmileage",
  primarypurpose: "adminprimaryuse",
  collisiondeductible: "admincolldeductible",
  comprehensivedeductible: "admincompdeductible",
  coverage: "admincoverage",
  leased: "adminleased",
  currentinsurer: "admincurrentinsurer",
  sr22: "adminsr22required",
  accidentsall: "adminaccidents",
  accidentsatfault: "adminaccidentsatfault",
  duis: "adminduis",
  tickets: "admintickets",
  allincidents: "adminallincidents",
}

export const canonicalKey = (value, type) => {
  const key = keyOf(value).replace(/^(click|lead|call)analytic/, "")
  if (type.startsWith("analytics")) {
    if (key === "adgroup") return "adgroupname"
    if (key === "ad") return "adname"
    // CSV exports both the consumer's range (sq_ft) and its numeric lower
    // bound (square_footage); these must never collapse into one column.
    if (String(value).trim().toLowerCase() === "square footage") return "sqft"
    if (key === "squarefootageinteger") return "squarefootage"
    if (ANALYTICS_COLUMN_ALIASES[key]) return ANALYTICS_COLUMN_ALIASES[key]
  }
  if (key === "campaign") return type.startsWith("analytics") ? "name" : "campaignname"
  if (["calls", "prospects"].includes(type) && key === "cost") return "price"
  return COLUMN_ALIASES[key] || key
}

const normalizeBoolean = (value, key) => {
  if ([true, "true", "t", "yes", "Yes"].includes(value)) return "yes"
  return key === "searchpro" ? "" : "no"
}

const normalizeNumeric = (text, key) => {
  const duration = text.match(/^(\d+):(\d{2}):(\d{2})$/)
  if (key === "callduration" && duration)
    return String(Number(duration[1]) * 3600 + Number(duration[2]) * 60 + Number(duration[3]))
  const number = text.replace(/[$,%]/g, "")
  if (/^-?\d+(\.\d+)?$/.test(number))
    return MONEY.has(key) ? Number(number).toFixed(2) : String(Number(number))
}

const dateFormatters = new Map()
const dateFormatter = timezone => {
  if (!dateFormatters.has(timezone)) {
    dateFormatters.set(
      timezone,
      new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    )
  }
  return dateFormatters.get(timezone)
}

const normalizeDate = (text, timezone) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const date = new Date(text)
    if (!Number.isNaN(date.getTime())) {
      const parts = dateFormatter(timezone)
        .formatToParts(date)
        .reduce((all, part) => ({ ...all, [part.type]: part.value }), {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`
    }
  }
  const date = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(am|pm))?$/i)
  if (date) {
    const hour = (Number(date[4] || 0) % 12) + (date[6]?.toLowerCase() === "pm" ? 12 : 0)
    return `${date[3]}-${date[1].padStart(2, "0")}-${date[2].padStart(2, "0")}${
      date[4] ? ` ${String(hour).padStart(2, "0")}:${date[5]}` : ""
    }`
  }
}

export const normalizedValue = (value, key = "", timezone = REPORTING_TIMEZONE) => {
  if (value === null || value === undefined || value === "" || value === "--") return ""
  const text = String(value).trim()
  if (key === "insured" || key === "searchpro") return normalizeBoolean(value, key)
  if (NUMERIC.has(key)) {
    const numeric = normalizeNumeric(text, key)
    if (numeric !== undefined) return numeric
  }
  // Export display formatters capitalize names. Preserve punctuation and IDs.
  return normalizeDate(text, timezone) ?? text.toLowerCase()
}

const enrichRow = (row, result) => {
  const detail = row.lead_detail || {}
  if (row.lead_detail) result.name = [detail.first_name, detail.last_name].filter(Boolean).join(" ")
  if (row.campaign)
    Object.assign(result, {
      campaign_name: row.campaign.name,
      brand_name: row.campaign.brand?.name,
    })
  if (row.brand) result.brand_name = row.brand.name
  if (row.lead_type_id)
    result.lead_type_name = row.lead_type_name || LEAD_TYPE_NAMES[row.lead_type_id]
  const business = row.lead_business_entities?.[0]
  if (business)
    Object.assign(result, business, {
      num_employees: detail.num_employees ?? business.num_full_time_employees,
    })
  return result
}

const flattenAnalytics = (row, type) => {
  const result = { ...row.lead_detail, ...row }
  // The SP click serializer exposes click_time/pst_day and exports plain
  // campaign names. Legacy analytics appends the campaign ID.
  const spClicks = type === "analytics_individual_clicks" && "click_time" in row && "pst_day" in row
  if (!spClicks && Object.prototype.hasOwnProperty.call(row, "campaign_id"))
    result.name = `${row.name || ""} (${row.campaign_id || ""})${
      row.campaign_discarded_at ? " (deleted)" : ""
    }`
  for (const key of ["city", "county"]) {
    if (typeof result[key] === "string") result[key] = result[key].trim().split(/\s+/).join(" ")
  }
  for (const key of [
    "admin_home_owner",
    "admin_insured",
    "admin_bankruptcy",
    "admin_leased",
    "admin_sr22_required",
  ]) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) continue
    const value = row[key]
    if (value == null || value === "") {
      result[key] = key === "admin_home_owner" ? "No" : ""
    } else {
      result[key] = boolText(value)
    }
  }
  for (const [key, affirmative] of [
    ["admin_license_status", "active"],
    ["admin_marital_status", "married"],
  ]) {
    if (Object.prototype.hasOwnProperty.call(row, key))
      result[key] = row[key] ? (String(row[key]).toLowerCase() === affirmative ? "Yes" : "No") : ""
  }
  for (const key of [
    "admin_num_vehicles",
    "admin_accidents",
    "admin_accidents_at_fault",
    "admin_duis",
    "admin_tickets",
    "admin_all_incidents",
  ]) {
    if (Object.prototype.hasOwnProperty.call(row, key) && !Number(row[key])) result[key] = ""
  }
  if (row.ad_group_discarded_at) result.ad_group_name = `${row.ad_group_name || ""} (deleted)`
  if (Object.prototype.hasOwnProperty.call(row, "position"))
    result.position = Math.trunc(Number(row.position || 0))
  return enrichRow(row, result)
}

const flattenLeads = (row, receiptTypes) => {
  const result = enrichRow(row, { ...row.lead_detail, ...row })
  result.refund_status = row.lead_refund?.status
  const debitTypes = new Set(
    receiptTypes.filter(t => t.is_debit === true || t.is_debit === "true").map(t => Number(t.id))
  )
  result.cost =
    row.lead_refund?.status?.toLowerCase() === "approved"
      ? 0
      : (row.receipts || [])
          .filter(r => debitTypes.has(Number(r.receipt_type_id)))
          .reduce((sum, r) => sum + Number(r.debit || 0), 0)
  return result
}

const flattenCallContact = row => {
  const detail = row.lead_detail || {}
  const result = enrichRow(row, { ...detail, ...row })
  result.insured = detail.insured === true || String(detail.insured).toLowerCase() === "true"
  return result
}

const flattenProspects = row => {
  const result = flattenCallContact(row)
  const calls = row.qf_quote_calls || []
  const latest = [...calls].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
  Object.assign(result, {
    status: calls.length > 1 ? "Multiple Calls" : latest?.status,
    call_duration: latest ? Number(latest.call_duration || 0) : null,
    recording_url: latest?.recording_url,
    applicants: row.lead_applicants?.length || 0,
    vehicles: row.lead_vehicles?.length || 0,
    disposition: row.disposition || "Received - not worked yet",
  })
  return result
}

const flattenCalls = row => {
  const detail = row.lead_detail || {}
  const result = flattenCallContact(row)
  // Contact fields come from the released lead detail; quote-call metadata differs.
  for (const key of ["phone", "address", "city", "state", "zip", "aid", "cid"])
    result[key] = detail[key]
  result.disposition = row.disposition || "Transferred"
  delete result.cost // Wholesale cost is not the exported client price.
  result.applicants = row.lead_applicants?.length || 0
  result.vehicles = row.lead_vehicles?.length || 0
  if (row.refund_status?.toLowerCase() === "approved") result.price = 0
  return result
}

const ROW_ADAPTERS = {
  leads: flattenLeads,
  calls: flattenCalls,
  prospects: flattenProspects,
}
export const flattenRow = (row, type, receiptTypes = []) => {
  // Top-level identity/time must win over nested detail metadata.
  if (type.startsWith("analytics")) {
    const result = flattenAnalytics(row, type)
    // Analytics CSV currency_formatter renders nil cost as $0.00.
    if (Object.prototype.hasOwnProperty.call(result, "cost") && result.cost == null)
      result.cost = 0
    return result
  }
  const adapter = ROW_ADAPTERS[type]
  return adapter ? adapter(row, receiptTypes) : enrichRow(row, { ...row.lead_detail, ...row })
}

const canonicalRow = (row, type) =>
  Object.fromEntries(
    Object.entries(row)
      .filter(([, value]) => typeof value !== "object" || value === null)
      .map(([key, value]) => [canonicalKey(key, type), value])
  )
const stable = value => {
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map(key => [key, stable(value[key])])
    )
  }
  return value
}
const contract = value =>
  Object.fromEntries(
    Object.entries(value || {})
      .filter(
        ([key]) =>
          ![
            "controller",
            "action",
            "page_number",
            "page_size",
            "validation_id",
            "analytic",
            "analytics_lead",
            "analytics_call",
            "admin_analytic",
            "lead",
            "call",
            "prospect",
            "is_admin_export",
          ].includes(key)
      )
      .map(([key, value]) => {
        if (key === "filters" && typeof value === "string") {
          try {
            return [key, JSON.parse(value)]
          } catch (error) {
            return [key, value]
          }
        }
        return [key, value]
      })
  )
export const persistedContract = params => {
  const top = contract(params)
  if (Object.keys(top).length) return top
  const nested = [
    "analytic",
    "analytics_lead",
    "analytics_call",
    "admin_analytic",
    "lead",
    "call",
    "prospect",
  ].find(key => params?.[key] && Object.keys(params[key]).length)
  return contract(nested ? params[nested] : {})
}

// IDs are compared verbatim: display normalization must not merge distinct records.
const indexExportRows = (rows, key) => {
  const records = new Map()
  let missing = 0
  let duplicates = 0
  rows.forEach(row => {
    const value = row[key]
    if (value == null || ["", "--"].includes(String(value).trim())) {
      missing += 1
      return
    }
    const id = String(value)
    if (records.has(id)) duplicates += 1
    else records.set(id, row)
  })
  return { records, missing, duplicates }
}

// Without identity, value differences are still conclusive, but equality is not a PASS.
const sameValueCounts = (left, right, valueOf) => {
  const counts = new Map()
  left.forEach(row => {
    const value = valueOf(row)
    counts.set(value, (counts.get(value) || 0) + 1)
  })
  for (const row of right) {
    const value = valueOf(row)
    const count = counts.get(value)
    if (!count) return false
    if (count === 1) counts.delete(value)
    else counts.set(value, count - 1)
  }
  return !counts.size
}

const compareExportRows = (page, exported, keys, exportType, differences) => {
  if (!page.length && !exported.length) return
  let idKey = exportType === "analytics_individual_clicks" ? "clickid" : "id"
  if (!keys.includes(idKey)) {
    // Use report-native keys, never row order or money values. Composite keys
    // must be populated and unique on both sides; ambiguity stays INCOMPLETE.
    const identityKeys = exportType === "analytics_individual_calls"
      ? ["pingid", "name"]
      : exportType === "analytics_individual_leads"
        ? ["timestamp", "name", "jornayaleadid"]
        : LIST_TYPES.includes(exportType)
          ? ["phone", "createdat", "campaignname"]
          : []
    if (identityKeys.length && identityKeys.every(key => keys.includes(key))) {
      idKey = "__recordkey"
      keys = [...keys, idKey]
      ;[page, exported].forEach(rows => rows.forEach(row => {
        const parts = identityKeys.map(key => normalizedValue(row[key], key))
        const required = exportType === "analytics_individual_leads" ? parts.slice(0, 2) : parts
        row[idKey] = required.every(Boolean) ? JSON.stringify(parts) : ""
      }))
      // Calls received within the same displayed minute can share the base
      // key. Their exported recording URL identifies the separate calls.
      // Refine the same collision groups on both sides, without using price,
      // duration or row order to decide which records match.
      if (exportType === "calls" && keys.includes("recordingurl")) {
        const collisions = new Set()
        ;[page, exported].forEach(rows => {
          const seen = new Set()
          rows.forEach(row => {
            const key = row[idKey]
            if (key && seen.has(key)) collisions.add(key)
            seen.add(key)
          })
        })
        ;[page, exported].forEach(rows => rows.forEach(row => {
          if (!collisions.has(row[idKey])) return
          const recording = String(row.recordingurl ?? "").trim()
          row[idKey] = recording && recording !== "--"
            ? JSON.stringify([row[idKey], recording]) : ""
        }))
      }
    }
  }
  const left = indexExportRows(page, idKey)
  const right = indexExportRows(exported, idKey)
  if (
    !keys.includes(idKey) ||
    left.missing ||
    right.missing ||
    left.duplicates ||
    right.duplicates
  ) {
    differences.identity_coverage = {
      message:
        "A shared, non-empty, unique record key is required to verify row-to-record associations.",
      key: idKey,
      page_missing_ids: left.missing,
      csv_missing_ids: right.missing,
      page_duplicate_ids: left.duplicates,
      csv_duplicate_ids: right.duplicates,
    }
    const valueKeys = keys.filter(key => key !== idKey)
    const signature = row => JSON.stringify(valueKeys.map(key => normalizedValue(row[key], key)))
    if (page.length === exported.length && !sameValueCounts(page, exported, signature)) {
      differences.row_values = {
        fields: valueKeys.filter(
          key => !sameValueCounts(page, exported, row => normalizedValue(row[key], key))
        ),
        message: "Mapped CSV values differ; record associations could not be verified.",
      }
    }
    return
  }

  const missing = [...left.records.keys()].filter(id => !right.records.has(id)).length
  const unexpected = [...right.records.keys()].filter(id => !left.records.has(id)).length
  if (missing || unexpected) differences.record_ids = { missing, unexpected }
  const fields = new Set()
  let mismatchedRecords = 0
  left.records.forEach((row, id) => {
    const csvRow = right.records.get(id)
    if (!csvRow) return
    const changed = keys.filter(
      key => normalizedValue(row[key], key) !== normalizedValue(csvRow[key], key)
    )
    if (!changed.length) return
    mismatchedRecords += 1
    changed.forEach(key => fields.add(key))
  })
  if (mismatchedRecords) {
    differences.row_values = {
      fields: [...fields],
      records: mismatchedRecords,
      message: "Mapped CSV values differ for the same record ID.",
    }
  }
}

export const compareExport = ({
  pageRows,
  csv,
  exportType,
  requestedPayload,
  exportParams,
  receiptTypes = [],
}) => {
  const differences = {}
  const page = pageRows.map(row =>
    canonicalRow(flattenRow(row, exportType, receiptTypes), exportType)
  )
  const exported = csv.rows.map(row => canonicalRow(row, exportType))
  const pageKeys = new Set(page.flatMap(row => Object.keys(row)))
  const csvKeys = csv.headers.map(key => canonicalKey(key, exportType))
  const missingReceiptTypes =
    exportType === "leads" && !receiptTypes.length && csvKeys.includes("cost")
  const keys = csvKeys.filter(key => pageKeys.has(key) && !(missingReceiptTypes && key === "cost"))
  const unsupported = csv.headers.filter(header => !pageKeys.has(canonicalKey(header, exportType)))
  if (page.length !== exported.length)
    differences.row_count = { page_api: page.length, csv: exported.length }
  if (new Set(csvKeys).size !== csvKeys.length) differences.duplicate_headers = csv.headers
  const required = LIST_TYPES.includes(exportType)
    ? [
        "name",
        "phone",
        "email",
        "createdat",
        "campaignname",
        "leadtypename",
        "brandname",
        "disposition",
      ]
    : ["clicks", "leads", "calls", "cost", "revenue", "impressions"].filter(key =>
        pageKeys.has(key)
      )
  const missing = required.filter(
    key => !(exportType === "calls" && key === "email") && !csvKeys.includes(key)
  )
  if (missing.length) differences.missing_columns = missing
  compareExportRows(page, exported, keys, exportType, differences)
  const formatting = csv.headers.filter(header => {
    const key = canonicalKey(header, exportType)
    if (!MONEY.has(key) && !["ctr", "winrate"].includes(key)) return false
    return csv.rows.some(row => {
      const value = String(row[header] ?? "").trim()
      if (!value || value === "--") return false
      return MONEY.has(key) ? !/^\$-?[\d,]+\.\d{2}$/.test(value) : !/^-?[\d.]+%$/.test(value)
    })
  })
  if (formatting.length) differences.formatting = formatting
  if (requestedPayload && !exportParams)
    differences.request_contract = "Persisted export parameters were not returned."
  if (
    requestedPayload &&
    exportParams &&
    JSON.stringify(stable(contract(requestedPayload))) !==
      JSON.stringify(stable(persistedContract(exportParams)))
  )
    differences.request_contract = {
      requested: contract(requestedPayload),
      persisted: persistedContract(exportParams),
    }
  if (page.length && unsupported.length) differences.unmapped_csv_headers = unsupported
  if (exportType === "leads" && page.length && !receiptTypes.length && csvKeys.includes("cost"))
    differences.coverage = "Receipt types are unavailable; cost cannot be independently verified."
  const mismatch = Object.keys(differences).some(
    key => !["unmapped_csv_headers", "coverage", "identity_coverage"].includes(key)
  )
  const incomplete =
    !!differences.identity_coverage ||
    !!differences.coverage ||
    (page.length > 0 && (unsupported.length > 0 || !keys.length))
  let status = page.length ? "PASS" : "NO_DATA"
  if (incomplete) status = "INCOMPLETE"
  if (mismatch) status = "DATA_MISMATCH"
  return {
    status,
    page_rows: page.length,
    csv_rows: exported.length,
    csv_headers: csv.headers,
    checked_columns: keys.length,
    differences,
  }
}

// A capped reference can prove a sampled discrepancy, never full export parity.
export const compareCappedExport = (options, reason) => {
  const idKey = options.exportType === "analytics_individual_clicks" ? "clickid" : "id"
  const header = options.csv.headers.find(key => canonicalKey(key, options.exportType) === idKey)
  const ids = options.pageRows.map(row => canonicalRow(row, options.exportType)[idKey])
  const usable = header && ids.every(id => id != null && String(id).trim() !== "") && new Set(ids.map(String)).size === ids.length
  if (!usable) return {
    status: "INCOMPLETE", page_rows: options.pageRows.length, csv_rows: options.csv.rows.length,
    differences: { coverage: reason, identity_coverage: "The capped reference has no unique shared ID for sample comparison." },
  }
  const referenceIds = new Set(ids.map(String))
  const sampled = options.csv.rows.filter(row => referenceIds.has(String(row[header])))
  const result = compareExport({ ...options, csv: { ...options.csv, rows: sampled } })
  return {
    ...result,
    status: result.status === "DATA_MISMATCH" ? "DATA_MISMATCH" : "INCOMPLETE",
    csv_rows: options.csv.rows.length,
    sampled_csv_rows: sampled.length,
    differences: { ...result.differences, coverage: reason, sampled_rows_checked: options.pageRows.length },
  }
}

export const buildPayload = ({ selectedType, dates, validationId }) => {
  const list = selectedType.recordSource || selectedType.value
  if (LIST_TYPES.includes(list)) {
    const range = { startDate: dates.start_date, endDate: dates.end_date }
    let filters = list === "prospects" ? [] : {}
    if (dates.start_date && dates.end_date) {
      filters =
        list === "prospects"
          ? [{ name: "received", value: { value: range } }]
          : { received_value: JSON.stringify(range) }
    }
    return {
      payload: {
        filters: JSON.stringify(filters),
        validation_id: validationId,
      },
    }
  }
  const payload = {
    ...dates,
    timezone: REPORTING_TIMEZONE,
    group_by_params: [],
    validation_id: validationId,
  }
  if (selectedType.productTypeId) payload.product_type_id = selectedType.productTypeId
  if (selectedType.individual) payload[selectedType.individual] = true
  return { payload }
}

export const detailExportParams = (row, id) => {
  const product = row?.product_type_id || row?.campaign?.product_type_id
  if (!row?.lead_type_id || !product)
    throw new Error(
      "Detail API did not return lead_type_id/product_type_id required by the export."
    )
  return { lead_id: id, lead_type_id: row.lead_type_id, product_type_id: product }
}

export const validateCsvResult = (text, result) => {
  if (!text.trim() || /^\s*(?:<!doctype|<html|\{|\[)/i.test(text))
    throw new Error("Download is not a CSV file (empty, HTML, or JSON response).")
  const errors = (result.errors || []).filter(error => error.code !== "UndetectableDelimiter")
  if (errors.length)
    throw new Error(`Malformed CSV: ${errors.map(error => error.message).join("; ")}`)
}

// Detail exports are presentation documents, not dumps of every API attribute.
// Compare their explicit section/field contract, including repeated drivers.
const boolText = value => {
  if (value == null) return ""
  const normalized = typeof value === "string" ? value.toLowerCase() : value
  return [true, "true", "t", "yes"].includes(normalized) ? "Yes" : "No"
}
const dateOnly = value => (value ? String(value).slice(0, 10) : "")
const detailSection = (name, fields) => [
  [name],
  ...Object.entries(fields).map(([key, value]) => [key.replace(/_/g, " "), value ?? ""]),
]

const buildDetailsSection = row => {
  const detail = row.lead_detail || {}
  const owner =
    detail.vehicle_registered_to_you === true
      ? {}
      : {
          vehicle_owner_first_name: detail.vehicle_owner_first_name,
          vehicle_owner_last_name: detail.vehicle_owner_last_name,
        }
  return detailSection("Details", {
    name: [detail.first_name, detail.last_name].filter(Boolean).join(" "),
    email: detail.email,
    phone: detail.phone,
    address: [detail.address || "", detail.city || "", detail.state || "", detail.zip || ""]
      .join(" ")
      .trim(),
    vehicle_registered_to_you: boolText(detail.vehicle_registered_to_you),
    ...owner,
  })
}

const buildCoverageSection = row => {
  const detail = row.lead_detail || {}
  return detailSection("Existing coverage", {
    currently_insured: boolText(detail.insured),
    policy_expires: dateOnly(detail.policy_expiration_dt),
    carrier: detail.insco,
    continuous_insurance: detail.continuous_insurance,
  })
}

const buildDriversSection = row => {
  const output = []
  const section = (name, fields) => output.push(...detailSection(name, fields))
  const detail = row.lead_detail || {}
  const applicants = row.lead_applicants || []
  if (applicants.length) output.push(["Drivers"])
  applicants.forEach((applicant, index) =>
    section(`Driver #${index + 1}`, {
      first_name: applicant.first_name,
      last_name: applicant.last_name,
      date_of_birth: dateOnly(applicant.dob),
      marital_status: applicant.marital_status,
      license_status: applicant.license_status,
      education: applicant.education,
      occupation: applicant.occupation,
      gender: applicant.gender,
      credit_rating: applicant.credit,
      relationship: applicant.relation,
      military: boolText(applicant.military_service),
      home_owner: index === 0 ? boolText(detail.own_home) : "",
      sr_22: index === 0 ? boolText(applicant.sr_22) : "",
      bankruptcy: index === 0 ? boolText(detail.bankruptcy) : "",
    })
  )
  return output
}

const buildViolationsSection = row => {
  const output = []
  const section = (name, fields) => output.push(...detailSection(name, fields))
  const applicants = row.lead_applicants || []
  applicants.forEach((applicant, index) => {
    const violations = (row.lead_violations || []).filter(
      violation => violation.lead_applicant_id === applicant.id
    )
    if (violations.length) output.push([`Violations for Driver #${index + 1}`])
    violations.forEach((violation, violationIndex) =>
      section(`Violation #${violationIndex + 1}`, {
        driver_name: [applicant.first_name, applicant.last_name].filter(Boolean).join(" "),
        date: dateOnly(violation.dt),
        description: violation.desc,
        amount_paid: Number(violation.paid_amount || 0),
        violation_type: findViolationTypeFromID(violation.violation_type_id),
      })
    )
  })
  return output
}

const buildVehiclesSection = row => {
  const output = []
  const section = (name, fields) => output.push(...detailSection(name, fields))
  const vehicles = row.lead_vehicles || []
  if (vehicles.length) output.push(["Vehicles"])
  vehicles.forEach((vehicle, index) =>
    section(`Vehicle #${index + 1}`, {
      vin: vehicle.vin,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      sub_model: vehicle.sub_model,
      vehicle_usage: vehicle.primary_use,
      ownership: vehicle.ownership_status,
      garage: vehicle.garage_type,
      miles_per_year: vehicle.annual_mileage,
    })
  )
  return output
}

const buildAdditionalInfoSection = row => {
  const product = Number(row.product_type_id || row.campaign?.product_type_id)
  const prefix = { 2: "Call", 5: "Prospect" }[product] || "Lead"
  const additional = {
    Type: LEAD_TYPE_NAMES[row.lead_type_id],
    Campaign: row.campaign?.name,
    [`${prefix} received`]: row.created_at,
  }
  if (row.disposition != null) additional.Disposition = row.disposition
  if (row.lead_refund)
    Object.assign(additional, {
      "Refund status": row.lead_refund.status,
      "Refund reason": row.lead_refund.lead_refund_reason?.reason,
      "Refund comments": row.lead_refund.comments,
    })
  if (row.receipts?.length)
    additional["Lead cost"] = row.receipts.reduce(
      (sum, receipt) => sum + Number(receipt.debit || 0),
      0
    )
  return detailSection("Additional Info", additional)
}

export const expectedHomeDetailRows = row => {
  const detail = row.lead_detail || {}
  const applicants = row.lead_applicants || []
  const output = [
    ...detailSection("Details", {
      name: [detail.first_name, detail.last_name].filter(Boolean).join(" "),
      email: detail.email, phone: detail.phone,
      address: [detail.address || "", detail.city || "", detail.state || "", detail.zip || ""].join(" ").trim(),
    }),
    ...buildCoverageSection(row),
  ]
  if (applicants.length) {
    output.push(["Applicants"])
    applicants.forEach((person, index) => output.push(...detailSection(`Applicant #${index + 1}`, {
      first_name: person.first_name, last_name: person.last_name,
      date_of_birth: dateOnly(person.dob), marital_status: person.marital_status,
      education: person.education, occupation: person.occupation, gender: person.gender,
      credit_rating: person.credit, military: boolText(person.military_service),
      home_owner: index === 0 ? boolText(detail.own_home) : "",
      sr_22: index === 0 ? boolText(person.sr_22) : "",
      bankruptcy: index === 0 ? boolText(detail.bankruptcy) : "",
    })))
    if (row.lead_homes?.length) output.push(["Homes"])
    ;(row.lead_homes || []).forEach((home, index) => output.push(...detailSection(`Home #${index + 1}`, {
      address: [home.address || "", home.city || "", home.state || "", home.zip || ""].join(" ").trim(),
      residence_type: home.residence_type, year_built: home.year_built, area: home.sq_ft,
      bedrooms: home.num_bedrooms, bathrooms: home.num_bathrooms,
      swimming_pool: boolText(home.swimming_pool), garage: home.garage_type,
      flood_zone: boolText(home.flood_zone), stories: home.num_stories, residents: home.num_residents,
    })))
    applicants.forEach((person, index) => {
      const claims = (row.lead_violations || []).filter(claim => claim.lead_applicant_id === person.id)
      if (claims.length) output.push([`Claims for Applicant #${index + 1}`])
      claims.forEach((claim, claimIndex) => output.push(...detailSection(`Claim #${claimIndex + 1}`, {
        description: claim.desc, date: dateOnly(claim.dt), amount_paid: Number(claim.paid_amount || 0),
      })))
    })
  }
  return [...output, ...buildAdditionalInfoSection(row)]
}

export const expectedAutoDetailRows = row => [
  ...buildDetailsSection(row),
  ...buildCoverageSection(row),
  ...buildDriversSection(row),
  ...buildViolationsSection(row),
  ...buildVehiclesSection(row),
  ...buildAdditionalInfoSection(row),
]

const detailFields = rows => {
  let section = ""
  let parent = ""
  return rows.reduce((all, row) => {
    if (row.length === 1) {
      if (/^(violations for driver|claims for applicant)/i.test(row[0])) parent = keyOf(row[0])
      else if (!/^(violation|claim) #/i.test(row[0])) parent = ""
      section = `${parent}/${keyOf(row[0])}`
      all.push([section, "section"])
    } else {
      const key = keyOf(row[0])
      all.push([`${section}/${key}`, normalizedValue(row[1], key)])
    }
    return all
  }, [])
}

export const compareDetailExport = (page, csvRows) => {
  if (![LEAD_TYPE_IDS.AUTO, LEAD_TYPE_IDS.MOTORCYCLE, LEAD_TYPE_IDS.HOME].includes(Number(page.lead_type_id)))
    return {
      status: "INCOMPLETE",
      differences: {
        coverage:
          "Detail export downloaded, but this lead type does not yet have a verified section/field comparison contract.",
      },
      csv_fields: csvRows.filter(row => row.length > 1).length,
    }
  const expected = Number(page.lead_type_id) === LEAD_TYPE_IDS.HOME ? expectedHomeDetailRows(page) : expectedAutoDetailRows(page)
  const left = detailFields(expected)
  const right = detailFields(csvRows)
  const mismatches = []
  const missingRefundReason =
    page.lead_refund?.lead_refund_reason_id && !page.lead_refund.lead_refund_reason
  const coverage = missingRefundReason
    ? "The page reference API does not expose this refund reason; its text cannot be independently verified."
    : null
  for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
    if (
      missingRefundReason &&
      left[index]?.[0] === "/additionalinfo/refundreason" &&
      right[index]?.[0] === left[index][0]
    )
      continue
    if (JSON.stringify(left[index]) !== JSON.stringify(right[index]))
      mismatches.push({
        expected_field: left[index]?.[0],
        csv_field: right[index]?.[0],
      })
  }
  let status = coverage ? "INCOMPLETE" : "PASS"
  if (mismatches.length) status = "DATA_MISMATCH"
  return {
    status,
    page_fields: expected.filter(row => row.length > 1).length,
    csv_fields: csvRows.filter(row => row.length > 1).length,
    differences: {
      ...(mismatches.length ? { field_values: mismatches } : {}),
      ...(coverage ? { coverage } : {}),
    },
  }
}
